import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ShowContainer from '../../../components/material-ui/ShowContainer';
import {
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import MyTableBoldCell from '../../../components/material-ui/adaptive/MyTableBoldCell';
import MyTableCell from '../../../components/material-ui/adaptive/MyTableCell';
import CusTooltip from '../../../components/material-ui/CusTooltip';
import { useAppSelector } from '../../../store';
import CurrencyValue from '../../../components/material-ui/CurrencyValue';
import CusSort from '../../../components/material-ui/CusSort';
import { ENUM_SORT_DIRECTION } from '../../../enums/enums';
import useSorting from '../../../hooks/useSorting';
import ShowContentWitchEmpty from '../../../components/material-ui/show_content_empty/ShowContentWitchEmpty';
import LoaderContainer from '../../../components/material-ui/loader_container/LoaderContainer';

/**
 * statistic
0 - название игры
1 - название провайдера игры
2 - название источника игры
3 - название валюты
4 - общее число запусков игры
5 - общее число спинов игры
6 - средняя ставка
7 - общая сумма ставок
8 - общая сумма выигрышей
9 - стоимость игры
10 - RTP, %
11 - GGR
12 - % комиссии провайдера
*/

export default function StatisticGames({ loader, empty }) {
  const { t } = useTranslation();
  const { sortText, sortNumeric } = useSorting();
  const statistic = useAppSelector((state) => state.games.statistic.result);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: ENUM_SORT_DIRECTION.asc });

  const total = {
    id: t('In total'),
    countRun: statistic.reduce((acc, el) => acc + el[4], 0),
    countSpin: statistic.reduce((acc, el) => acc + el[5], 0),
    average: statistic.reduce((acc, el) => acc + (el[6] || 0), 0),
    sumBet: statistic.reduce((acc, el) => acc + el[7], 0),
    sumWin: statistic.reduce((acc, el) => acc + el[8], 0),
    costGame: statistic.reduce((acc, el) => acc + el[9], 0),
    RTP: statistic.reduce((acc, el) => acc + (el[10] || 0), 0),
    averagePercent: statistic.reduce((acc, el) => acc + (el[12] || 0), 0) / statistic.length
  };

  const isLess = true;
  const currency = statistic?.[0]?.[3];

  const averageRTP = +((total.sumWin / total.sumBet) * 100).toFixed?.(2);

  const MyCurrencyValue = ({ value, ...props }) => (
    <CurrencyValue
      justifyContent={'end'}
      value={value}
      currency={currency}
      {...props}
      fontSize={12}
    />
  );

  const MyHeadCell = ({ text, children, name, sx }) => (
    <MyTableBoldCell isLess sx={sx}>
      <Stack direction={'row'} gap={0} alignItems={'center'} minWidth={80}>
        <Typography fontSize={12} fontWeight={600} lineHeight={'110%'}>
          {children || t(text)}
        </Typography>
        <CusSort
          name={name}
          type={sortConfig.key == name ? sortConfig.direction : null}
          setSortConfig={setSortConfig}
        />
      </Stack>
    </MyTableBoldCell>
  );

  const sortKey = sortConfig.key;
  const sortDirection = sortConfig.direction;

  const statisticList = useMemo(() => {
    if (sortKey == 'name') return sortText(statistic, 0, sortDirection);
    if (sortKey == 'provider') return sortText(statistic, 1, sortDirection);
    if (sortKey == 'providerCommission') return sortNumeric(statistic, 13, sortDirection);
    if (sortKey == 'pool') return sortText(statistic, 2, sortDirection);
    if (sortKey == 'launches') return sortNumeric(statistic, 4, sortDirection);
    if (sortKey == 'spins') return sortNumeric(statistic, 5, sortDirection);
    if (sortKey == 'average') return sortNumeric(statistic, 6, sortDirection);
    if (sortKey == 'betAmount') return sortNumeric(statistic, 7, sortDirection);
    if (sortKey == 'winnings') return sortNumeric(statistic, 8, sortDirection);
    if (sortKey == 'costGame') return sortNumeric(statistic, 9, sortDirection);
    if (sortKey == 'RTP') return sortNumeric(statistic, 10, sortDirection);
    if (sortKey == 'GGR') return sortNumeric(statistic, 11, sortDirection);
    return statistic;
  }, [sortKey, sortDirection, statistic]);

  return (
    <LoaderContainer loader={loader} isBox>
      <ShowContentWitchEmpty
        condition={statistic.length}
        conditionPlug={empty}
        plugText="No entries found. Try changing the filters.">
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <MyTableBoldCell isLess>#</MyTableBoldCell>
                <MyHeadCell text={'Name'} name={'name'} sx={{ minWidth: 150 }} />
                <MyHeadCell text={'Provider'} name={'provider'} sx={{ minWidth: 100 }} />
                <MyHeadCell
                  text={'Commission'}
                  name={'providerCommission'}
                  sx={{ minWidth: 100 }}
                />
                <MyHeadCell text={'Pool'} name={'pool'} sx={{ minWidth: 100 }} />
                <MyHeadCell text={'Launches'} name={'launches'} />
                <MyHeadCell text={'Spins'} name={'spins'} />
                <MyHeadCell text={'Average rate'} name={'average'} />
                <MyHeadCell text={'Bet amount'} name={'betAmount'} />
                <MyHeadCell text={'Amount of winnings'} name={'winnings'} />
                <MyHeadCell text={'Cost of the game'} name={'costGame'} />
                <MyHeadCell text={'RTP, %'} name={'RTP'} />
                <MyHeadCell name={'GGR'}>
                  <Stack direction={'row'} justifyContent={'end'} alignItems={'center'} width={65}>
                    {t('GGR')}
                    <CusTooltip
                      text={
                        'The difference between the total bet in a game and the total winnings. GGR = total bet in a game - total winnings'
                      }
                    />
                  </Stack>
                </MyHeadCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {statisticList.map((statistic, indx) => {
                const currency = statistic[3];
                const average = statistic[6] ? statistic[6] + ' ' + currency : '';

                return (
                  <TableRow key={indx} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                    <MyTableCell isLess={isLess}>{indx + 1}</MyTableCell>
                    <MyTableCell isLess={isLess}>{statistic[0]}</MyTableCell>
                    <MyTableCell isLess={isLess}>{statistic[1]}</MyTableCell>
                    <MyTableCell isLess={isLess} type="numeric">
                      {statistic[12]}
                    </MyTableCell>
                    <MyTableCell isLess={isLess}>{statistic[2]}</MyTableCell>
                    <MyTableCell isLess={isLess} type="numeric">
                      {statistic[4]}
                    </MyTableCell>
                    <MyTableCell isLess={isLess} type="numeric">
                      {statistic[5]}
                    </MyTableCell>
                    <MyTableCell isLess={isLess} type="numeric">
                      {average}
                    </MyTableCell>
                    <MyTableCell isLess={isLess}>
                      <MyCurrencyValue value={statistic[7]} />
                    </MyTableCell>
                    <MyTableCell isLess={isLess}>
                      <MyCurrencyValue value={statistic[8]} />
                    </MyTableCell>
                    <MyTableCell isLess={isLess}>
                      <MyCurrencyValue value={statistic[9]} />
                    </MyTableCell>
                    <MyTableCell isLess={isLess} type="numeric">
                      {statistic[10]}
                    </MyTableCell>
                    <MyTableCell isLess={isLess}>
                      <MyCurrencyValue value={statistic[11]} />
                    </MyTableCell>
                  </TableRow>
                );
              })}

              <TableRow>
                <MyTableBoldCell colSpan={3} isLess sx={{ py: 2 }}>
                  {total.id}
                </MyTableBoldCell>
                <MyTableBoldCell isLess type="numeric">
                  {+total.averagePercent.toFixed?.(2)}
                </MyTableBoldCell>
                <MyTableBoldCell isLess sx={{ py: 2 }}></MyTableBoldCell>
                <MyTableBoldCell isLess type="numeric">
                  {+total.countRun.toFixed?.(2)}
                </MyTableBoldCell>
                <MyTableBoldCell isLess type="numeric">
                  {+total.countSpin.toFixed?.(2)}
                </MyTableBoldCell>
                <MyTableBoldCell isLess type="numeric">
                  <ShowContainer condition={total.sumBet / total.countSpin}>
                    <MyCurrencyValue value={total.sumBet / total.countSpin} fontWeight={700} />
                  </ShowContainer>
                </MyTableBoldCell>
                <MyTableBoldCell isLess>
                  <MyCurrencyValue value={total.sumBet} fontWeight={700} />
                </MyTableBoldCell>
                <MyTableBoldCell isLess>
                  <MyCurrencyValue value={total.sumWin} fontWeight={700} />
                </MyTableBoldCell>
                <MyTableBoldCell isLess>
                  <MyCurrencyValue value={total.costGame} fontWeight={700} />
                </MyTableBoldCell>
                <MyTableBoldCell isLess type="numeric">
                  <ShowContainer condition={averageRTP}>{averageRTP + '%' || ''}</ShowContainer>
                </MyTableBoldCell>
                <MyTableBoldCell isLess>
                  <MyCurrencyValue value={total.sumBet - total.sumWin} fontWeight={700} />
                </MyTableBoldCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </ShowContentWitchEmpty>
    </LoaderContainer>
  );
}
